import { esc, stars, highlight, formatOwnerText } from '../lib/helpers.js';
import { initLightbox, initPhotoStrips } from '../lib/gallery.js';

(() => {
  const data = window.__PAGE_DATA;
  if (!data || !data.reviewData) return;

  const PLACE_ID = data.placeId;
  const __REVIEW_DATA = data.reviewData;
  window.__REVIEW_DATA = __REVIEW_DATA;

  // Init gallery
  initLightbox();
  initPhotoStrips();

  // Show "Show more" only on review bodies that are actually clamped
  document.querySelectorAll('.review-body').forEach((p) => {
    if (p.scrollHeight > p.clientHeight + 2) {
      const label = p.parentElement.querySelector('.review-show-more');
      if (label) label.hidden = false;
    }
  });

  // ── Staff Highlights ──
  const renderStaff = (staffList) => {
    const container = document.getElementById('staff-list');
    const section = document.getElementById('staff-section');
    if (!container || !section) return;
    let h = '';
    staffList.forEach((s) => {
      const sentiment = s.sentiment || 'neutral';
      if (sentiment === 'positive') {
        h += `<button type="button" data-staff-name="${esc(s.name)}" class="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-3 py-1 text-sm font-medium cursor-pointer hover:bg-emerald-100 hover:border-emerald-300 transition-colors" title="Search reviews for ${esc(s.name)}">+${esc(s.name)} <span class="text-emerald-400 font-normal text-xs">(${s.count})</span></button>`;
      } else if (sentiment === 'negative') {
        h += `<button type="button" data-staff-name="${esc(s.name)}" class="inline-flex items-center gap-1 bg-red-50 text-red-600 border border-red-200 rounded-full px-3 py-1 text-sm font-medium cursor-pointer hover:bg-red-100 hover:border-red-300 transition-colors" title="Search reviews for ${esc(s.name)}">&minus;${esc(s.name)} <span class="text-red-300 font-normal text-xs">(${s.count})</span></button>`;
      } else {
        h += `<button type="button" data-staff-name="${esc(s.name)}" class="inline-flex items-center gap-1 bg-slate-100 text-slate-600 border border-slate-200 rounded-full px-3 py-1 text-sm font-medium cursor-pointer hover:bg-slate-200 hover:border-slate-300 transition-colors" title="Search reviews for ${esc(s.name)}">${esc(s.name)} <span class="text-slate-400 font-normal text-xs">(${s.count})</span></button>`;
      }
    });
    container.innerHTML = h;
    section.style.display = '';

    container.querySelectorAll('[data-staff-name]').forEach((pill) => {
      pill.addEventListener('click', () => {
        const searchInput = document.getElementById('rev-search');
        if (searchInput && window._revSetQuery) {
          searchInput.value = pill.dataset.staffName;
          window._revSetQuery(pill.dataset.staffName, 'staff');
          searchInput.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });
    });
  };

  // ── Tag Cloud ──
  const renderTags = (tagSummary) => {
    const cloud = document.getElementById('tag-cloud');
    const section = document.getElementById('tag-section');
    if (!cloud || !section) return;
    let h = '';
    tagSummary.forEach((tag) => {
      const sentimentClass = { positive: 'badge-success', neutral: 'badge-ghost', negative: 'badge-error' }[tag.sentiment] || 'badge-ghost';
      h += `<button class="badge ${sentimentClass} badge-outline gap-1 cursor-pointer hover:opacity-80 transition-opacity tag-btn" data-tag="${esc(tag.tag)}" data-sentiment="${esc(tag.sentiment)}">${esc(tag.tag)} <span class="opacity-60">(${tag.count})</span></button>`;
    });
    cloud.innerHTML = h;
    section.style.display = '';
  };

  // ── AI Review Summary ──
  const renderSummary = (summary) => {
    const container = document.getElementById('review-summary-container');
    if (!container) return;
    let h = '<div class="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6">';
    h += '<div class="flex items-center gap-2 mb-2.5">';
    h += '<svg class="w-3.5 h-3.5 text-blue-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z"/></svg>';
    h += '<span class="text-xs font-semibold text-blue-500 uppercase tracking-wider">AI Summary</span>';
    h += '</div>';
    if (summary.overview) h += `<p class="text-sm text-slate-700 leading-relaxed mb-2.5">${esc(summary.overview)}</p>`;
    h += '<div class="flex flex-wrap gap-x-6 gap-y-1">';
    (summary.positives || []).forEach((item) => {
      h += `<span class="inline-flex items-center gap-1.5 text-xs text-emerald-700"><svg class="w-3 h-3 text-emerald-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clip-rule="evenodd"/></svg>${esc(item)}</span>`;
    });
    (summary.concerns || []).forEach((item) => {
      h += `<span class="inline-flex items-center gap-1.5 text-xs text-red-600"><svg class="w-3 h-3 text-red-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clip-rule="evenodd"/></svg>${esc(item)}</span>`;
    });
    h += '</div></div>';
    container.innerHTML = h;
  };

  // ── Notable Reviews ──
  const renderNotable = (notableList) => {
    const section = document.getElementById('notable-section');
    if (!section) return;
    let topPositive = null, topNegative = null;
    for (let i = 0; i < notableList.length; i++) {
      const r = notableList[i];
      if (!topPositive && r.rating >= 4) topPositive = r;
      if (!topNegative && r.rating <= 3) topNegative = r;
      if (topPositive && topNegative) break;
    }
    const top5 = [];
    if (topPositive) top5.push(topPositive);
    if (topNegative) top5.push(topNegative);
    if (top5.length === 0) return;
    let h = '<h3 class="text-base font-semibold text-slate-700 flex items-center gap-2 mb-3">'
      + '<svg class="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 0 0 .95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 0 0-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 0 0-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 0 0-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 0 0 .951-.69l1.07-3.292z"/></svg>'
      + 'Notable Reviews</h3><div class="space-y-4">';
    top5.forEach((rev, idx) => {
      const avatarSrc = rev.profile_image_r2 || rev.profile_image;
      const date = rev.reviewed_at ? rev.reviewed_at.slice(0, 10) : '';
      const tags = (rev.tags || []).join(',');
      const isPositive = rev.rating >= 4;
      const borderColor = isPositive ? 'border-green-400' : 'border-red-400';
      const bgColor = isPositive ? 'bg-green-50' : 'bg-red-50';
      const outerBorder = isPositive ? 'border-green-200' : 'border-red-200';
      h += `<div class="notable-rev relative border-l-4 ${borderColor} ${bgColor} border ${outerBorder} rounded-xl p-5" data-text="${esc(rev.review_text || '')}" data-tags="${esc(tags)}">`;
      h += '<div class="flex items-center gap-3 mb-3">';
      if (avatarSrc) {
        h += `<img src="${esc(avatarSrc)}" alt="${esc(rev.profile_name || 'Reviewer')}" class="w-9 h-9 rounded-full object-cover flex-shrink-0 bg-slate-100" loading="lazy" onerror="this.style.display='none'">`;
      }
      h += `<div><p class="font-semibold text-sm text-slate-800">${esc(rev.profile_name || 'Anonymous')}</p>`;
      h += `<div class="flex items-center gap-2"><span class="text-amber-400 text-sm">${stars(rev.rating)}</span>`;
      h += `<span class="text-xs text-slate-400">${esc(date)}</span>`;
      if (rev.reviewer_reviews_count) h += `<span class="text-xs text-slate-400">&middot; ${rev.reviewer_reviews_count} reviews</span>`;
      h += '</div></div></div>';
      if (rev.review_text) {
        const toggleId = `ntgl-${idx}`;
        h += `<input type="checkbox" class="notable-toggle" id="${toggleId}">`;
        h += `<p class="notable-body notable-rev-text text-sm text-slate-700 leading-relaxed">${esc(rev.review_text)}</p>`;
        h += `<label for="${toggleId}" class="notable-show-more text-xs font-medium text-blue-600 hover:text-blue-800 cursor-pointer mt-1 inline-block">Show more &darr;</label>`;
        h += `<label for="${toggleId}" class="notable-show-less text-xs font-medium text-blue-600 hover:text-blue-800 cursor-pointer mt-1 inline-block">Show less &uarr;</label>`;
      }
      if (rev.review_images && rev.review_images.length) {
        const imgJson = JSON.stringify(rev.review_images).replace(/"/g, '&quot;');
        h += '<div class="flex flex-wrap gap-2 mt-3">';
        rev.review_images.forEach((url, i) => {
          const imgBorder = isPositive ? 'border-green-200 hover:border-green-400' : 'border-red-200 hover:border-red-400';
          h += `<img src="${esc(url)}" alt="Review photo" class="h-20 w-28 object-cover rounded-lg border ${imgBorder} hover:opacity-85 transition-all duration-150 cursor-pointer" loading="lazy" onclick="lbOpen(${imgJson},${i})" onerror="this.remove()">`;
        });
        h += '</div>';
      }
      if (rev.owner_answer) {
        h += '<div class="mt-4 ml-4 pl-4 border-l-2 border-slate-200">';
        h += '<p class="text-xs font-semibold text-slate-500 mb-1">Owner response';
        if (rev.owner_timestamp) h += ` &middot; <span class="font-normal">${esc(rev.owner_timestamp.slice(0, 10))}</span>`;
        h += `</p><div class="rev-owner-wrap"><p class="rev-owner-text rev-clamped text-sm text-slate-600 leading-relaxed" style="-webkit-line-clamp:3">${formatOwnerText(rev.owner_answer)}</p>`;
        h += '<button class="rev-owner-more" style="display:none;font-size:12px;color:#2563eb;background:none;border:none;padding:2px 0;cursor:pointer;margin-top:2px">Show more</button>';
        h += '<button class="rev-owner-less" style="display:none;font-size:12px;color:#2563eb;background:none;border:none;padding:2px 0;cursor:pointer;margin-top:2px">Show less</button>';
        h += '</div></div>';
      }
      h += '</div>';
    });
    h += '</div>';
    section.innerHTML = h;
    section.style.display = '';

    requestAnimationFrame(() => {
      section.querySelectorAll('.notable-body').forEach((p) => {
        const label = p.nextElementSibling;
        if (label && label.classList.contains('notable-show-more') && p.scrollHeight > p.clientHeight + 2) {
          label.style.display = 'inline';
        }
      });
      section.querySelectorAll('.rev-owner-wrap').forEach((wrap) => {
        const text = wrap.querySelector('.rev-owner-text');
        const more = wrap.querySelector('.rev-owner-more');
        const less = wrap.querySelector('.rev-owner-less');
        if (!text || !more || !less) return;
        if (text.scrollHeight > text.clientHeight + 2) more.style.display = 'inline';
        more.addEventListener('click', () => {
          text.classList.remove('rev-clamped');
          more.style.display = 'none';
          less.style.display = 'inline';
        });
        less.addEventListener('click', () => {
          text.classList.add('rev-clamped');
          less.style.display = 'none';
          more.style.display = 'inline';
        });
      });
    });
  };

  // ── Review List (sort, filter, paginate) ──
  const initReviewList = (ALL) => {
    const PAGE = 5;
    let sort = 'date-desc';
    let page = 1;
    let query = '';
    let queryMode = 'search';
    let activeTag = '';
    let notableOnly = false;
    let ratingFilter = 0;

    const list = document.getElementById('rev-list');
    const pgn = document.getElementById('rev-pagination');
    const prev = document.getElementById('rev-prev');
    const next = document.getElementById('rev-next');
    const info = document.getElementById('rev-info');
    const countEl = document.getElementById('rev-count');
    const searchInput = document.getElementById('rev-search');
    const notableOnlyBtn = document.getElementById('notable-only-btn');
    const tagCloud = document.getElementById('tag-cloud');
    const tagClearRow = document.getElementById('tag-clear-row');
    const tagClearBtn = document.getElementById('tag-clear-btn');

    window._revSetQuery = (q, mode = 'search') => {
      query = q;
      queryMode = mode;
      page = 1; filterNotable(query); render();
    };

    const ratingPill = document.getElementById('rating-filter-pill');
    window._revSetRatingFilter = (star) => {
      ratingFilter = (ratingFilter === star) ? 0 : star;
      page = 1;
      if (ratingPill) {
        if (ratingFilter) {
          ratingPill.textContent = `${ratingFilter} \u2605  \u2715`;
          ratingPill.style.display = '';
        } else {
          ratingPill.style.display = 'none';
        }
      }
      render();
    };
    if (ratingPill) {
      ratingPill.addEventListener('click', () => {
        window._revSetRatingFilter(ratingFilter);
      });
    }

    if (notableOnlyBtn) {
      notableOnlyBtn.addEventListener('click', () => {
        notableOnly = !notableOnly;
        notableOnlyBtn.classList.toggle('btn-primary', notableOnly);
        notableOnlyBtn.classList.toggle('btn-outline', !notableOnly);
        page = 1; render();
      });
    }

    const cardHtml = (rev) => {
      const date = rev.reviewed_at ? rev.reviewed_at.slice(0, 10) : '';
      let h = '<div class="bg-white border border-slate-200 rounded-xl p-4">';
      h += '<div class="flex items-center gap-2.5 mb-2">';
      const avatarSrc = rev.profile_image_r2 || rev.profile_image;
      if (avatarSrc) {
        h += `<img src="${esc(avatarSrc)}" alt="${esc(rev.profile_name)}" class="w-8 h-8 rounded-full object-cover flex-shrink-0 bg-slate-100" loading="lazy" onerror="this.style.display='none'">`;
      }
      h += '<div class="flex-1 min-w-0">';
      h += `<p class="font-semibold text-xs text-slate-800 truncate">${highlight(rev.profile_name || 'Anonymous', query)}</p>`;
      h += '<div class="flex items-center gap-1.5">';
      h += `<span style="color:#f59e0b;font-size:12px">${stars(rev.rating)}</span>`;
      h += `<span class="text-xs" style="color:#94a3b8">${esc(date)}</span>`;
      h += '</div></div></div>';
      if (rev.review_text) {
        h += '<div>';
        if (query) {
          h += `<p class="rev-body text-sm" style="color:#475569;line-height:1.625">${highlight(rev.review_text, query)}</p>`;
        } else {
          h += `<p class="rev-body rev-clamped text-sm" style="color:#475569;line-height:1.625">${esc(rev.review_text)}</p>`;
          h += '<button class="rev-show-more" style="display:none;font-size:12px;color:#2563eb;background:none;border:none;padding:2px 0;cursor:pointer;margin-top:2px">Show more</button>';
          h += '<button class="rev-show-less" style="display:none;font-size:12px;color:#2563eb;background:none;border:none;padding:2px 0;cursor:pointer;margin-top:2px">Show less</button>';
        }
        h += '</div>';
      }
      if (rev.review_images && rev.review_images.length) {
        const imgJson = JSON.stringify(rev.review_images).replace(/"/g, '&quot;');
        h += '<div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:10px">';
        rev.review_images.forEach((url, i) => {
          h += `<img src="${esc(url)}" alt="Review photo" style="height:72px;width:100px;object-fit:cover;border-radius:8px;border:1px solid #e2e8f0;cursor:pointer" loading="lazy" onclick="lbOpen(${imgJson},${i})" onerror="this.remove()">`;
        });
        h += '</div>';
      }
      const ownerText = rev.owner_answer || (window._ownerResponses && window._ownerResponses[rev.id]) || '';
      if (ownerText) {
        h += '<div style="margin-top:10px;margin-left:10px;padding-left:10px;border-left:2px solid #f1f5f9">';
        h += '<p style="font-size:11px;font-weight:600;color:#94a3b8;margin-bottom:2px">Owner response</p>';
        if (query) {
          h += `<p style="font-size:12px;color:#64748b;line-height:1.5">${highlight(ownerText, query)}</p>`;
        } else {
          h += `<div class="rev-owner-wrap"><p class="rev-owner-text rev-clamped" style="font-size:12px;color:#64748b;line-height:1.5;-webkit-line-clamp:2">${formatOwnerText(ownerText)}</p>`;
          h += '<button class="rev-owner-more" style="display:none;font-size:12px;color:#2563eb;background:none;border:none;padding:2px 0;cursor:pointer;margin-top:2px">Show more</button>';
          h += '<button class="rev-owner-less" style="display:none;font-size:12px;color:#2563eb;background:none;border:none;padding:2px 0;cursor:pointer;margin-top:2px">Show less</button></div>';
        }
        h += '</div>';
      }
      h += '</div>';
      return h;
    };

    const getSorted = () => {
      const a = ALL.slice();
      if (sort === 'date-desc') a.sort((x, y) => { return (y.reviewed_at || '') < (x.reviewed_at || '') ? -1 : 1; });
      if (sort === 'date-asc') a.sort((x, y) => { return (x.reviewed_at || '') < (y.reviewed_at || '') ? -1 : 1; });
      if (sort === 'rating-desc') a.sort((x, y) => { return (y.rating || 0) - (x.rating || 0); });
      if (sort === 'rating-asc') a.sort((x, y) => { return (x.rating || 0) - (y.rating || 0); });
      return a;
    };

    const getFiltered = () => {
      const sorted = getSorted();
      const lq = query ? query.toLowerCase() : '';
      return sorted.filter((r) => {
        const haystack = queryMode === 'staff'
          ? `${r.review_text || ''}`
          : `${r.review_text || ''} ${r.profile_name || ''} ${r.owner_answer || ''}`;
        const textMatch = !lq || haystack.toLowerCase().indexOf(lq) !== -1;
        const tagMatch = !activeTag || (Array.isArray(r.tags) && r.tags.indexOf(activeTag) !== -1);
        const notableMatch = !notableOnly || (r.notable_score != null && r.notable_score >= 8);
        const ratingMatch = !ratingFilter || r.rating === ratingFilter;
        return textMatch && tagMatch && notableMatch && ratingMatch;
      });
    };

    const filterNotable = (q) => {
      const notableSec = document.getElementById('notable-section');
      const notableRevs = notableSec ? notableSec.querySelectorAll('.notable-rev') : [];
      let anyVisible = false;
      notableRevs.forEach((div) => {
        const text = div.dataset.text || '';
        const tags = (div.dataset.tags || '').split(',').filter(Boolean);
        const textMatch = !q || text.toLowerCase().indexOf(q.toLowerCase()) !== -1;
        const tagMatch = !activeTag || tags.indexOf(activeTag) !== -1;
        const visible = textMatch && tagMatch;
        div.style.display = visible ? '' : 'none';
        if (visible) anyVisible = true;
        const textEl = div.querySelector('.notable-rev-text');
        if (textEl) textEl.innerHTML = highlight(text, q);
      });
      if (notableSec) notableSec.style.display = anyVisible ? '' : 'none';
    };

    const checkClamp = () => {
      list.querySelectorAll('.rev-body').forEach((p) => {
        const showMore = p.nextElementSibling;
        if (showMore && p.scrollHeight > p.clientHeight + 2) showMore.style.display = 'inline';
      });
      list.querySelectorAll('.rev-owner-wrap').forEach((wrap) => {
        const text = wrap.querySelector('.rev-owner-text');
        const more = wrap.querySelector('.rev-owner-more');
        const less = wrap.querySelector('.rev-owner-less');
        if (!text || !more || !less) return;
        if (text.scrollHeight > text.clientHeight + 2) more.style.display = 'inline';
        more.addEventListener('click', () => {
          text.classList.remove('rev-clamped');
          more.style.display = 'none';
          less.style.display = 'inline';
        });
        less.addEventListener('click', () => {
          text.classList.add('rev-clamped');
          less.style.display = 'none';
          more.style.display = 'inline';
        });
      });
    };

    const render = () => {
      const filtered = getFiltered();
      const total = filtered.length;
      const pages = Math.max(1, Math.ceil(total / PAGE));
      page = Math.min(page, pages);
      const start = (page - 1) * PAGE;
      const end = Math.min(start + PAGE, total);
      list.innerHTML = filtered.slice(start, end).map(cardHtml).join('');
      if (!query) requestAnimationFrame(checkClamp);
      if ((query || activeTag) && total !== ALL.length) {
        countEl.textContent = `${total} of ${ALL.length} shown`;
      } else {
        countEl.textContent = `(${ALL.length})`;
      }
      if (total === 0) list.innerHTML = '<p class="text-sm text-slate-400 py-4 text-center">No reviews match your search.</p>';
      info.textContent = total ? `${start + 1}\u2013${end} of ${total}` : '';
      pgn.style.cssText = total > PAGE ? 'display:flex!important' : 'display:none!important';
      prev.disabled = page <= 1;
      next.disabled = page >= pages;
    };

    document.querySelectorAll('[data-sort]').forEach((btn) => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('[data-sort]').forEach((b) => { b.classList.remove('btn-primary'); b.classList.add('btn-ghost'); });
        btn.classList.remove('btn-ghost'); btn.classList.add('btn-primary');
        sort = btn.dataset.sort;
        page = 1; render();
      });
    });

    prev.addEventListener('click', () => {
      if (page > 1) { page--; render(); list.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }
    });
    next.addEventListener('click', () => {
      const pages = Math.ceil(getFiltered().length / PAGE);
      if (page < pages) { page++; render(); list.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }
    });

    list.addEventListener('click', (e) => {
      if (e.target.classList.contains('rev-show-more')) {
        const p = e.target.previousElementSibling;
        p.classList.remove('rev-clamped');
        e.target.style.display = 'none';
        e.target.nextElementSibling.style.display = 'inline';
      }
      if (e.target.classList.contains('rev-show-less')) {
        const p = e.target.previousElementSibling.previousElementSibling;
        p.classList.add('rev-clamped');
        e.target.style.display = 'none';
        e.target.previousElementSibling.style.display = 'inline';
      }
    });

    const setActiveTag = (tag) => {
      activeTag = tag;
      if (tagCloud) {
        tagCloud.querySelectorAll('.tag-btn').forEach((btn) => {
          const isActive = btn.dataset.tag === tag;
          btn.classList.toggle('badge-outline', !isActive);
          btn.classList.toggle('opacity-80', !isActive);
        });
      }
      if (tagClearRow) tagClearRow.classList.toggle('hidden', !tag);
      page = 1; filterNotable(query); render();
    };
    if (tagCloud) {
      tagCloud.addEventListener('click', (e) => {
        const btn = e.target.closest('.tag-btn');
        if (!btn) return;
        const tag = btn.dataset.tag || '';
        setActiveTag(tag === activeTag ? '' : tag);
      });
    }
    if (tagClearBtn) {
      tagClearBtn.addEventListener('click', () => { setActiveTag(''); });
    }

    let searchTimer;
    if (searchInput) {
      searchInput.addEventListener('input', () => {
        clearTimeout(searchTimer);
        searchTimer = setTimeout(() => {
          query = searchInput.value.trim();
          queryMode = 'search';
          page = 1; filterNotable(query); render();
        }, 200);
      });
    }

    render();

    // Allow business-auth.js to trigger re-render after owner responses load
    window._revRerender = () => { render(); };
  };

  // ── Charts ──
  const renderCharts = (chartData, reviews) => {
    document.getElementById('charts-section').style.display = '';
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4/dist/chart.umd.min.js';
    script.onload = () => {
      const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const labels = chartData.map((d) => {
        const parts = d.month.split('-');
        const mo = parseInt(parts[1], 10) - 1;
        return `${MONTHS[mo]} '${parts[0].slice(2)}`;
      });
      const ratings = chartData.map((d) => { return d.avg_rating; });
      const counts = chartData.map((d) => { return d.count; });
      const commonOpts = {
        responsive: true, maintainAspectRatio: true,
        plugins: { legend: { display: false } },
        scales: {
          x: { grid: { display: false }, ticks: { font: { size: 10 }, color: '#94a3b8' } },
          y: { grid: { color: '#f1f5f9' }, ticks: { font: { size: 10 }, color: '#94a3b8' } },
        },
      };
      new Chart(document.getElementById('chart-rating'), {
        type: 'line',
        data: { labels: labels, datasets: [{ data: ratings, borderColor: '#3b82f6', backgroundColor: 'rgba(59,130,246,0.08)', borderWidth: 2, pointRadius: 3, pointBackgroundColor: '#3b82f6', fill: true, tension: 0 }] },
        options: Object.assign({}, commonOpts, { layout: { padding: { top: 8 } }, scales: Object.assign({}, commonOpts.scales, { y: Object.assign({}, commonOpts.scales.y, { min: 1, max: 5, ticks: Object.assign({}, commonOpts.scales.y.ticks, { stepSize: 1 }) }) }) }),
      });
      new Chart(document.getElementById('chart-volume'), {
        type: 'bar',
        data: { labels: labels, datasets: [{ data: counts, backgroundColor: '#94a3b8', hoverBackgroundColor: '#475569', borderRadius: 4 }] },
        options: commonOpts,
      });
      const dist = [0, 0, 0, 0, 0];
      reviews.forEach((r) => { if (r.rating >= 1 && r.rating <= 5) dist[r.rating - 1]++; });
      const distData = [dist[4], dist[3], dist[2], dist[1], dist[0]];
      const distColors = ['#22c55e', '#84cc16', '#eab308', '#f97316', '#ef4444'];
      const starLabels = ['\u2605\u2605\u2605\u2605\u2605', '\u2605\u2605\u2605\u2605', '\u2605\u2605\u2605', '\u2605\u2605', '\u2605'];
      const distWrap = document.getElementById('dist-chart-wrap');
      const distCanvas = document.getElementById('chart-distribution');
      if (distWrap && distCanvas) {
        distWrap.style.display = '';
        const distChart = new Chart(distCanvas, {
          type: 'bar',
          data: {
            labels: starLabels,
            datasets: [{ data: distData, backgroundColor: distColors, borderRadius: 3, barThickness: 8, maxBarThickness: 8 }]
          },
          options: {
            indexAxis: 'y',
            responsive: true, maintainAspectRatio: false,
            plugins: { legend: { display: false }, tooltip: { callbacks: { title: (items) => { return `${5 - items[0].dataIndex} star`; }, label: (item) => { return `${item.raw} reviews`; } } } },
            scales: {
              x: { display: false },
              y: { grid: { display: false }, ticks: { font: { size: 11 }, color: '#f59e0b', padding: 2, autoSkip: false } },
            },
            layout: { padding: { top: 0, bottom: 0 } },
            onHover: (e, elements) => { distCanvas.style.cursor = elements.length ? 'pointer' : 'default'; },
          },
        });
        distCanvas.addEventListener('click', (e) => {
          const points = distChart.getElementsAtEventForMode(e, 'nearest', { intersect: true }, false);
          if (points.length && window._revSetRatingFilter) {
            const star = 5 - points[0].index;
            window._revSetRatingFilter(star);
          }
        });
      }
    };
    document.head.appendChild(script);
  };

  // ── Load review data (after all functions are defined) ──
  if (__REVIEW_DATA) {
    if (__REVIEW_DATA.staff_highlights && __REVIEW_DATA.staff_highlights.length) renderStaff(__REVIEW_DATA.staff_highlights);
    if (__REVIEW_DATA.tag_summary && __REVIEW_DATA.tag_summary.length) renderTags(__REVIEW_DATA.tag_summary);
    if (__REVIEW_DATA.review_summary) renderSummary(__REVIEW_DATA.review_summary);
    if (__REVIEW_DATA.notable_reviews && __REVIEW_DATA.notable_reviews.length) renderNotable(__REVIEW_DATA.notable_reviews);
    if (__REVIEW_DATA.reviews && __REVIEW_DATA.reviews.length) {
      document.getElementById('reviews-section').style.display = '';
      initReviewList(__REVIEW_DATA.reviews);
    }
    if (__REVIEW_DATA.chart_data && __REVIEW_DATA.chart_data.length > 1) renderCharts(__REVIEW_DATA.chart_data, __REVIEW_DATA.reviews || []);
  }
})();
